import { FC, useEffect, useState } from 'react';
import { IconButton, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { MplButton } from '../../atoms/MplButton';
import { ChevronIcon } from '../../icons/Chevron';
import { MplBottomSheet } from '../MplBottomSheet/MplBottomSheet';

import { buildMonthGrid, isSameDay, isWithin } from './calendarGrid';
import {
	CalendarHeader,
	CalendarWrapper,
	DayCell,
	DayCircle,
	DayGrid,
	EmptyCell,
	FooterRow,
	MonthLabel,
	WeekdayRow,
	WeekRow,
} from './MplCalendarSheet.styled';

export interface IMplCalendarSheetProps {
	open: boolean;
	/** Currently committed start date (ISO 'YYYY-MM-DD') or null. */
	value: string | null;
	/** Earliest selectable date, ISO. */
	minDate: string;
	/** Latest selectable date, ISO. */
	maxDate: string;
	/** Returns true if a given ISO day may be selected (e.g. Mondays-only). */
	isSelectable: (isoDate: string) => boolean;
	/** Optional: given a selected ISO day, the inclusive range to highlight. */
	getHighlightRange?: (
		isoDate: string
	) => { start: string; end: string } | null;
	/** ISO date considered "today" for the today-marker (injected for testability). */
	today: string;
	onConfirm: (isoDate: string) => void;
	onCancel: () => void;
	labels: { cancel: string; confirm: string };
	testId?: string;
}

const WEEKDAY_HEADERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const toMonthStart = (iso: string): string =>
	dayjs(iso).startOf('month').format('YYYY-MM-DD');

export const MplCalendarSheet: FC<IMplCalendarSheetProps> = ({
	open,
	value,
	minDate,
	maxDate,
	isSelectable,
	getHighlightRange,
	today,
	onConfirm,
	onCancel,
	labels,
	testId = 'mpl-calendar-sheet',
}) => {
	const [viewMonth, setViewMonth] = useState<string>(
		toMonthStart(value ?? today)
	);
	const [draft, setDraft] = useState<string | null>(value);

	// Reset internal state only on the open transition; reading latest value/today at open time is intended
	useEffect(() => {
		if (open) {
			setViewMonth(toMonthStart(value ?? today));
			setDraft(value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- reset only on the open transition; reading latest value/today at open time is intended
	}, [open]);

	const weeks = buildMonthGrid(viewMonth);

	const minMonth = toMonthStart(minDate);
	const maxMonth = toMonthStart(maxDate);
	const isPrevDisabled = viewMonth <= minMonth;
	const isNextDisabled = viewMonth >= maxMonth;

	const highlightRange =
		draft && getHighlightRange ? getHighlightRange(draft) : null;

	const handlePrevMonth = () => {
		setViewMonth(dayjs(viewMonth).subtract(1, 'month').format('YYYY-MM-DD'));
	};

	const handleNextMonth = () => {
		setViewMonth(dayjs(viewMonth).add(1, 'month').format('YYYY-MM-DD'));
	};

	const canConfirm =
		!!draft && isSelectable(draft) && isWithin(draft, minDate, maxDate);

	const handleConfirm = () => {
		if (canConfirm) onConfirm(draft);
	};

	return (
		<MplBottomSheet open={open} onClose={onCancel} showPuller testId={testId}>
			<CalendarWrapper>
				{/* Header: month label + nav arrows */}
				<CalendarHeader>
					<MonthLabel>
						<Typography variant="subtitle1" fontWeight={600}>
							{dayjs(viewMonth).format('MMMM YYYY')}
						</Typography>
					</MonthLabel>
					<MonthLabel>
						<IconButton
							size="small"
							onClick={handlePrevMonth}
							disabled={isPrevDisabled}
							aria-label="previous month"
						>
							<ChevronIcon direction="left" fontSize="small" />
						</IconButton>
						<IconButton
							size="small"
							onClick={handleNextMonth}
							disabled={isNextDisabled}
							aria-label="next month"
						>
							<ChevronIcon direction="right" fontSize="small" />
						</IconButton>
					</MonthLabel>
				</CalendarHeader>

				{/* Weekday header row: Monday-first */}
				<WeekdayRow>
					{WEEKDAY_HEADERS.map((letter, i) => (
						<Typography
							key={i}
							variant="caption"
							align="center"
							color="text.secondary"
							sx={{ display: 'block', py: 0.5 }}
						>
							{letter}
						</Typography>
					))}
				</WeekdayRow>

				{/* Day grid */}
				<DayGrid>
					{weeks.map((week, wi) => (
						<WeekRow key={wi}>
							{week.map((iso, di) => {
								if (iso === null) {
									// Blank leading/trailing cells are plain spacers (no tint)
									return <EmptyCell key={`null-${wi}-${di}`} />;
								}

								const selectable =
									isSelectable(iso) && isWithin(iso, minDate, maxDate);
								const isToday = isSameDay(iso, today);
								const isSelected = isSameDay(iso, draft);
								const inRange =
									!!highlightRange &&
									isWithin(iso, highlightRange.start, highlightRange.end);

								return (
									<DayCell
										key={iso}
										ownerState={{
											disabled: !selectable,
										}}
										disabled={!selectable}
										onClick={selectable ? () => setDraft(iso) : undefined}
										aria-label={dayjs(iso).format('dddd, D. MMMM YYYY')}
										aria-pressed={isSelected}
									>
										<DayCircle
											ownerState={{
												isSelected,
												isToday,
												inRange: inRange && !isSelected,
												disabled: !selectable,
											}}
										>
											{dayjs(iso).date()}
										</DayCircle>
									</DayCell>
								);
							})}
						</WeekRow>
					))}
				</DayGrid>

				{/* Footer */}
				<FooterRow>
					<MplButton variant="text" onClick={onCancel}>
						{labels.cancel}
					</MplButton>
					<MplButton
						variant="text"
						onClick={handleConfirm}
						disabled={!canConfirm}
					>
						{labels.confirm}
					</MplButton>
				</FooterRow>
			</CalendarWrapper>
		</MplBottomSheet>
	);
};
